import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { formatDuration } from "date-fns";

import { Icon } from "@/components/icon/icon";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useLocale, useTranslation } from "@/lib/i18n";

import { RefreshContext } from "./context";
import { RefreshTimer } from "./types";
import * as Styled from "./styles";

export const RefreshButton = () => {
  const refreshTimer = useContext(RefreshContext);

  if (!refreshTimer) return null;
  if (!refreshTimer.isActive) return null;

  return <RefreshButtonInner {...refreshTimer} />;
};

const RefreshButtonInner = ({
  refreshNow,
  isRefreshing,
  lastRefreshedAt,
  autoRefreshInterval,
}: RefreshTimer) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const [progress, setProgress] = useState(0);
  const [secondsToNextRefresh, setSecondsToNextRefresh] = useState(autoRefreshInterval);

  const formattedTimeRemaining = useMemo(() => {
    return formatDuration(
      {
        minutes: Math.floor(secondsToNextRefresh / 60),
        seconds: secondsToNextRefresh % 60,
      },
      { locale: getDateFnsLocale(locale) },
    );
  }, [secondsToNextRefresh, locale]);

  const animate = useCallback(() => {
    const timePassedSinceLastRefresh = Date.now() - lastRefreshedAt.getTime();
    const timeToNextRefresh = autoRefreshInterval - timePassedSinceLastRefresh;
    setSecondsToNextRefresh(Math.ceil(timeToNextRefresh / 1000));
    setProgress((timePassedSinceLastRefresh / autoRefreshInterval) * 100);
  }, [autoRefreshInterval, lastRefreshedAt]);

  useAnimationFrame(animate);

  return (
    <Styled.Tooltip
      title={
        isRefreshing
          ? t("dashboard.refreshTimer.refreshing")
          : t("dashboard.refreshTimer.autoRefreshTooltip", {
              duration: formattedTimeRemaining,
            })
      }
    >
      <div>
        <Styled.RefreshButton
          onClick={refreshNow}
          disabled={isRefreshing}
          variant="primary"
        >
          <Styled.RefreshProgress
            value={progress}
            variant={isRefreshing ? "indeterminate" : "determinate"}
            color="inherit"
            size={28}
          />
          <Icon name="refresh-ccw" size={16} />
        </Styled.RefreshButton>
      </div>
    </Styled.Tooltip>
  );
};

const useAnimationFrame = (callback: (dt: number) => void) => {
  const requestRef = useRef<number>(0);
  const previousTimeRef = useRef<number>();

  const animate = (time: number) => {
    if (previousTimeRef.current != undefined) {
      const deltaTime = time - previousTimeRef.current;
      callback(deltaTime);
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [callback]);
};
